import React from "react";
import { Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { Table, TD, TH, TR } from "@ag-media/react-pdf-table";
import _, { get } from "lodash";
import { getFooter } from "../../../utils/helperUtils";
import { formatNumberPdf } from "../../../utils/formattingUtils";

const styles = StyleSheet.create({
    page: {
        padding: "40px 50px",
        backgroundColor: "#ffffff",
        position: "relative",
    },
    headerSection: {
        textAlign: "center",
        padding: "10px",
    },
    mainHeading: {
        fontSize: 14,
        fontWeight: "bold",
        marginBottom: 6,
        textAlign: "center",
        color: "#2c3e50",
        paddingBottom: 3,
    },
    subHeading: {
        fontSize: 12,
        marginBottom: 5,
        color: "#34495e",
    },
    address: {
        fontSize: 10,
        color: "#7f8c8d",
        marginBottom: 3,
    },
    tableSection: {
        marginBottom: 20,
        border: "1px solid #e0e0e0",
        borderRadius: 4,
        padding: 10,
        backgroundColor: "#f8f9fa",
    },
    tableCell: {
        fontSize: 8,
        padding: "4px 6px",
        borderBottom: "1px solid #e0e0e0",
        textAlign: "left",
    },
    tableHeader: {
        backgroundColor: "#f7f6f8",
        fontWeight: "bold",
        fontSize: 11,
        padding: "6px",
    },
    summaryRow: {
        backgroundColor: "#f7f6f8",
        fontWeight: "bold",
    },
    continuationFooter: {
        position: "absolute",
        bottom: 30,
        left: 50,
        right: 50,
        fontSize: 8,
        textAlign: "center",
        color: "#95a5a6",
        fontStyle: "italic",
    },
    pageNumber: {
        position: "absolute",
        bottom: 30,
        right: 50,
        fontSize: 8,
        color: "#95a5a6",
    },
});

interface TableRowData {
    sourceNo: number;
    sourceName: string;
    size: string;
    capRate: string;
    vacancy: string;
    annualRent: string;
}

interface SpaceData {
    enable?: boolean;
    spaceDetails: {
        spaceType: string;
    };
    marketCapVacancyRentalRate: any;
    incomeExpenseProForma: any;
}

interface TableSectionProps {
    title?: string;
    subtitle?: string;
    children: React.ReactNode;
}

const TableSection: React.FC<TableSectionProps> = ({ title, subtitle, children }) => (
    <View style={styles.tableSection} wrap={false}>
        {title && <Text style={styles.mainHeading}>{title}</Text>}
        {subtitle && <Text style={styles.subHeading}>{subtitle}</Text>}
        {children}
    </View>
);

const PropertyHeader = ({ data }) => {
    const address = _.get(data, "appealApproachCalculationData.exportAppealsPackage.coverPage");

    return (
        <View style={styles.headerSection}>
            <Text style={styles.mainHeading}>{address.subjectPropertyNumber}</Text>
            <Text style={styles.address}>{address.subjectPropertyAddress.streetaddress}</Text>
            <Text style={styles.address}>
                {`${address.subjectPropertyAddress.city}, ${address.subjectPropertyAddress.state} ${address.subjectPropertyAddress.zipCode}`}
            </Text>
        </View>
    );
};

const getTableRows = (space: SpaceData): TableRowData[] => {
    const lists = [
        "marketCapVacancyRentalRate.marketCapRate.list",
        "marketCapVacancyRentalRate.vacancy.list",
        "marketCapVacancyRentalRate.rentalRate.list",
    ];

    return lists
        .flatMap(path => _.get(space, path, []))
        .map((item, index) => ({
            sourceNo: index + 1,
            sourceName: item.sourceName,
            size: formatNumberPdf(item.sizeSqFt),
            capRate: formatNumberPdf(item.capRateUnLoaded),
            vacancy: formatNumberPdf(item.vacancy),
            annualRent: formatNumberPdf(item.annualRentPerSqFt),
        }));
};

const splitTableData = (rows: TableRowData[], rowsPerPage: number): TableRowData[][] => {
    const chunks: TableRowData[][] = [];
    for (let i = 0; i < rows.length; i += rowsPerPage) {
        chunks.push(rows.slice(i, i + rowsPerPage));
    }

    return chunks;
};

interface MarketRatesTableProps {
    rows: TableRowData[];
    showHeaders: boolean;
    isLastPage: boolean;
    space: SpaceData;
}

interface MarketCapVacancyRentalRatePageProps {
    data: any;
    confidentialInfo: string;
}

const MarketRatesTable: React.FC<MarketRatesTableProps> = ({
    rows,
    showHeaders = true,
    isLastPage = false,
    space,
}) => (
    <Table style={{ border: "none" }}>
        <TH>
            <TD
                style={{
                    ...styles.tableCell,
                    ...styles.tableHeader,
                    width: "100%",
                    textAlign: "left",
                    borderRight: "none", // Remove right border
                }}
            >
                {_.get(space, "spaceDetails.spaceType")}
            </TD>
        </TH>
        {showHeaders && (
            <TR>
                {[
                    "Source No.",
                    "Source Name",
                    "Size (SF)",
                    "Cap Rate (Unloaded)",
                    "Vacancy %",
                    "Annual Rent/SF",
                ].map((header, index) => (
                    <TD
                        key={index}
                        style={{ ...styles.tableCell, maxWidth: index === 0 ? "10%" : "auto" }}
                    >
                        {header}
                    </TD>
                ))}
            </TR>
        )}

        {rows.map((row, idx) => (
            <TR key={idx}>
                {Object.values(row).map((value, index) => (
                    <TD
                        key={index}
                        style={{ ...styles.tableCell, maxWidth: index === 0 ? "10%" : "auto" }}
                    >
                        {value}
                    </TD>
                ))}
            </TR>
        ))}

        {isLastPage && (
            <TR style={styles.summaryRow}>
                <TD style={{ ...styles.tableCell, maxWidth: "10%" }}></TD>
                <TD style={styles.tableCell}>Indicated Market Rates for Subject:</TD>
                <TD style={styles.tableCell}></TD>
                <TD style={styles.tableCell}>
                    {formatNumberPdf(_.get(space, "incomeExpenseProForma.unloadedCap"))}
                </TD>
                <TD style={styles.tableCell}>
                    {formatNumberPdf(
                        _.get(space, "incomeExpenseProForma.operatingIncome.lessMarketVacancy")
                    )}
                </TD>
                <TD style={styles.tableCell}>
                    {formatNumberPdf(
                        _.get(space, "marketCapVacancyRentalRate.rentalRate.reconciled")
                    )}
                </TD>
            </TR>
        )}
    </Table>
);

export const MarketCapVacancyRentalRatePage: React.FC<MarketCapVacancyRentalRatePageProps> = ({
    data,
    confidentialInfo,
}) => {
    const ROWS_PER_PAGE = 15;
    const spaces = _.get(data, "appealApproachCalculationData.income.spaces", {});

    const enabledSpaces = Object.keys(spaces)
        .map(key => spaces[key])
        .filter(space => get(space, "enable") !== false)
        .map(space => space);

    return (
        <>
            {enabledSpaces.map((space, spaceIndex) => {
                const tableRows = getTableRows(space);
                const tableChunks = splitTableData(tableRows, ROWS_PER_PAGE);

                return tableChunks.map((rows, pageIndex) => (
                    <Page key={`${spaceIndex}-${pageIndex}`} size="A4" style={styles.page}>
                        <View wrap={false}>
                            {pageIndex === 0 && (
                                <>
                                    <PropertyHeader data={data} />
                                    <Text style={styles.mainHeading}>
                                        Market Cap, Vacancy & Rental Rate Resources
                                    </Text>
                                    <MarketRatesTable
                                        rows={rows}
                                        showHeaders={true}
                                        isLastPage={pageIndex === tableChunks.length - 1}
                                        space={space}
                                    />
                                </>
                            )}

                            {pageIndex > 0 && (
                                <TableSection>
                                    <MarketRatesTable
                                        rows={rows}
                                        showHeaders={true}
                                        isLastPage={pageIndex === tableChunks.length - 1}
                                        space={space}
                                    />
                                </TableSection>
                            )}
                        </View>

                        {pageIndex === tableChunks.length - 1 ? (
                            getFooter(confidentialInfo)
                        ) : (
                            <View style={styles.continuationFooter} fixed>
                                <Text>
                                    {`Continued on next page (Page ${pageIndex + 1} of ${tableChunks.length})`}
                                </Text>
                            </View>
                        )}

                        <View style={styles.pageNumber} fixed>
                            <Text>{`${pageIndex + 1}/${tableChunks.length}`}</Text>
                        </View>
                    </Page>
                ));
            })}
        </>
    );
};
